import '.a{display:flex;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{width:100vw;}.ssr-plugin'
import '.e{height:100vh;}.ssr-plugin'
import '.f{background:rgba(1,1,37,1);}.ssr-plugin'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { styled } from 'inlines'
import { useQuery } from '@based/sdk'
import { Language } from '@/apps/cms/components/SiteLanguages'
import useLocalStorage from '@based/use-local-storage'
import { useDependency } from '@based/ui'
import { Spinner } from '../components/Spinner'
import { getLanguageName } from '../utils/getLanguageName'

export type AppCtxProps = {
  content?: 'live-content' | 'preview-content'
  languages: Language[]
  activeLanguage: string
  votes: Record<number, number>
  votesCount: number
  setVotes: React.Dispatch<React.SetStateAction<Record<number, number>>>
  price: number
  currency: string
  maxVotes: number
  minVotes?: number
}

const AppCtx = createContext<AppCtxProps>({
  content: 'live-content',
  languages: [],
  activeLanguage: 'en',
  votes: {},
  votesCount: 0,
  setVotes: () => {},
  price: 99,
  currency: 'eur',
  maxVotes: 20,
  minVotes: 0,
})

export const useAppCtx = () => useContext(AppCtx)

export const AppCtxProvider: FC<
  { children: ReactNode } & Partial<Pick<AppCtxProps, 'content'>>
> = ({ children, content }) => {
  const detectedContent =
    content ||
    (typeof window !== 'undefined' &&
    window.location.pathname.includes('/preview')
      ? 'preview-content'
      : 'live-content')
  const [storageActiveLanguage] = useLocalStorage('language')
  // @ts-ignore
  const editionWindow = window?.DATA?.edition
  const {
    data: config = {},
    checksum: configChecksum,
    loading: configLoading,
    error: configError,
  } = useQuery(editionWindow !== undefined ? 'edition-config' : '', {
    editionId: editionWindow,
    $$lang: storageActiveLanguage ?? 'en',
  })

  const {
    data: sequence = {},
    loading: sequenceLoading,
    error: sequenceError,
  } = useQuery(editionWindow !== undefined ? detectedContent : '', {
    id: editionWindow,
    content: 'sequence',
    lang: storageActiveLanguage ?? 'en',
  })

  const [votes, setVotes] = useLocalStorage(
    editionWindow && sequence?.id
      ? `votes-${editionWindow}-${sequence?.id}-${detectedContent}`
      : undefined,
  )
  const votesCount = Object.values(
    (votes || {}) as AppCtxProps['votes'],
  ).reduce((a, b) => a + b, 0)

  const languages: any[] = []
  for (const lang of config.languages || []) {
    // this makes storing the label in the DB kind of pointless but what can you do
    languages.push({ ...lang, label: getLanguageName(lang.value) })
  }

  const ctx: AppCtxProps = useMemo(
    () => ({
      content: detectedContent,
      languages,
      activeLanguage: storageActiveLanguage,
      votes: votes ?? {},
      votesCount,
      setVotes,
      currency: config.currency,
      maxVotes: config.maxVotes,
      minVotes: config.minVotes,
      price: config.price,
    }),
    [
      detectedContent,
      configChecksum,
      storageActiveLanguage,
      useDependency(votes || {}),
      votesCount,
      setVotes,
    ],
  )

  if (configLoading || configError || sequenceLoading || sequenceError) {
    return (
      <div className="a b c d e f"
      >
        <Spinner variant="white" />
      </div>
    )
  }

  return <AppCtx.Provider value={ctx}>{children}</AppCtx.Provider>
}
